import { useEffect, useMemo } from 'react'
import { useEntityRealtime } from '@/lib/realtime'
import { subscribeToQueryInvalidation } from '@/lib/realtime/shared-query-invalidation'
import { useQueryClient } from '@tanstack/react-query'

import type { useNotificationReadSession } from './useNotificationReadSession'

/** Socket bodies and counts are hints, never authorized reader results. */
export function useNotificationReaderRealtime(
  prefix: readonly unknown[],
  session: ReturnType<typeof useNotificationReadSession>,
  enabled = true,
) {
  const client = useQueryClient()
  const key = useMemo(() => [...prefix, session.key], [prefix, session])
  const organizationId = session.enabled ? session.organizationId : null
  const { addEventListener } = useEntityRealtime(
    'organization',
    organizationId,
    { suppressLogging: true },
  )
  useEffect(() => {
    if (!enabled || !organizationId) return
    const invalidation = subscribeToQueryInvalidation(
      client,
      organizationId,
      key,
    )
    const refresh = () => {
      try {
        session.assertCurrent()
        invalidation.schedule()
      } catch {
        /* Retired listener. */
      }
    }
    const cleanups = [
      'NOTIFICATION_CREATED',
      'NOTIFICATION_UPDATED',
      'NOTIFICATION_DELETED',
      'UNREAD_COUNT_UPDATED',
    ].map(event => addEventListener(event, refresh))
    return () => {
      cleanups.forEach(cleanup => cleanup())
      invalidation.dispose()
    }
  }, [client, key, session, organizationId, enabled, addEventListener])
}
